import { useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { updateQuestion, createQuestion } from '../service/brc'
import { useFormValidation, Validations } from '../service/formHelper';
import { AlertDismissable } from './Alerts'
import brandMetricTypes from '../config/dropdowns/brandMetricType.json'

const emptyAnswer = { answer: '', isCorrect: false };

const PREFERRED_HINT_ID = 'answers-preferred-hint';

// The preferred and the remove column are laid out at a fixed width so the header that carries the
// requiredness indicator lines up with the checkboxes underneath it
const PREFERRED_COLUMN_STYLE = { width: 170 };
const REMOVE_COLUMN_STYLE = { width: 56 };

const initialValues = {
  brandMetricType: '',
  question: '',
  isMultiple: false,
  isActive: true,
  answers: [{ ...emptyAnswer }, { ...emptyAnswer }]
};

// A question is always saved together with its answers, on creation and on edit alike, so that the
// preferred-answer requirement has a single point of enforcement: Brand Lift is the share of
// respondents who picked a preferred answer, so a question without one carries no lift
const validationRules = {
  question: [
    (value) => Validations.isEmpty(value, 'Question cannot be empty'),
  ],
  answers: [
    (answers) => answers?.length ? null : 'A question needs at least one answer',
    (answers) => answers?.every((a) => !Validations.isEmpty(a.answer))
      ? null
      : 'Every answer must have a text',
    (answers) => answers?.some((a) => a.isCorrect)
      ? null
      : 'Mark at least one answer as preferred — it is required to calculate lift',
  ],
};

const QuestionForm = props => {
  const [isLoading, setLoading] = useState(false)
  const [showAlert, setAlert] = useState(false)
  const [isError, setError] = useState(false)
  const [message, setMessage] = useState('Unknown Error!')

  const { values, errors, setValues, setErrors, handleChange, handleSubmit } = useFormValidation(
    initialValues,
    validationRules
  );

  useEffect(() => {
    if (props.show) {
      setValues({
        ...props,
        isMultiple: props.isMultiple ?? initialValues.isMultiple,
        isActive: props.isActive ?? initialValues.isActive,
        answers: props.answers ?? initialValues.answers,
      });
    }
  }, [props, setValues]);

  const dismissAlert = () => setAlert(false)

  const answers = values.answers ?? [];

  const setAnswers = (nextAnswers) => {
    setValues(prevValues => ({ ...prevValues, answers: nextAnswers }));
    setErrors(prevErrors => ({ ...prevErrors, answers: '' }));
  }

  const changeAnswer = (index, patch) =>
    setAnswers(answers.map((answer, i) => (i === index ? { ...answer, ...patch } : answer)));

  const addAnswer = () => setAnswers([...answers, { ...emptyAnswer }]);

  const removeAnswer = (index) => setAnswers(answers.filter((_, i) => i !== index));

  const onSubmit = async formData => {

      setLoading(true)
      let reqData = {
        ...formData,
        campaignId: props.campaignId,
        viewCount: props.isEdit ? null : 0,
        submitCount: props.isEdit ? null : 0,
        exposedSubmit: props.isEdit ? null : 0,
        exposedView: props.isEdit ? null : 0,
        controlSubmit: props.isEdit ? null : 0,
        controlView: props.isEdit ? null : 0,
        email: props.sessionUser.user,
        password: props.userPassword
      }

      let response = {}

      if (props.isEdit) {
        response = await updateQuestion(reqData, props.id)
      } else {
        response = await createQuestion(reqData)
      }

      if (response && response.data) {
        if (response.data._status === true) {
          setError(false)
          setMessage(response.data._message)
          setAlert(true)
          handleClose()
          props.loadQuestions();
        } else {
          setError(true)
          setMessage(response.data._message)
          setAlert(true)
        }
        setLoading(false)
      }
    },
    handleClose = () => {
      dismissAlert()
      props.setShow(false)
    }

  return (
    <Modal show={props.show} onHide={handleClose} backdrop='static' keyboard={false}>
      <Modal.Header closeButton closeLabel='' style={{ borderRadius: 5 }}>
        {props.isEdit && <Modal.Title>Edit Question</Modal.Title>}
        {!props.isEdit && <Modal.Title>Create Question</Modal.Title>}
      </Modal.Header>
      <Modal.Body>
        <AlertDismissable
          dismiss={dismissAlert}
          show={showAlert}
          message={message}
          type={isError ? 'error' : 'success'}
        />
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className='mb-3' controlId='industry'>
            <Form.Label>Brand Metric Type</Form.Label>
            <Form.Select
              required
              aria-label='Industry Selectbox'
              name="brandMetricType"
              value={values.brandMetricType}
              onChange={handleChange}>
              <option value=''>Please Choose Brand Metric Type</option>
              {brandMetricTypes.map((type, index) => (
                <option value={type} key={index}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className='mb-3' controlId='question'>
            <Form.Label>Question</Form.Label>
            <Form.Control
              required
              as='textarea'
              rows={3}
              name='question'
              value={values.question}
              isInvalid={!!errors.question}
              onChange={handleChange}
            />
            <Form.Control.Feedback type="invalid">{errors?.question}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className='mb-3' controlId='isMultiple'>
            <Form.Label>Multiple Answers</Form.Label>
            <Form.Check
              type='checkbox'
              id='isMultiple'
              name="isMultiple"
              checked={values.isMultiple}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className='mb-3' controlId='isActive'>
            <Form.Label>Active</Form.Label>
            <Form.Check
              type='checkbox'
              id='isActive'
              name="isActive"
              checked={values.isActive}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className='mb-3' controlId='answers'>
            <Row className='mb-2 align-items-start'>
              <Col>
                <Form.Label className='mb-0'>Answers</Form.Label>
              </Col>
              <Col xs='auto' style={PREFERRED_COLUMN_STYLE}>
                <div className='form-label mb-0'>
                  Preferred{' '}
                  <span className='text-danger' aria-hidden='true'>
                    *
                  </span>
                </div>
                <div className='text-danger small' id={PREFERRED_HINT_ID}>
                  At least one preferred answer is needed.
                </div>
              </Col>
              <Col xs='auto' style={REMOVE_COLUMN_STYLE} />
            </Row>
            {answers.map((answer, index) => (
              <Row className='mb-2 align-items-center' key={answer._id ?? `new-${index}`}>
                <Col>
                  <Form.Control
                    type='text'
                    aria-label={`Answer ${index + 1}`}
                    placeholder={`Answer ${index + 1}`}
                    value={answer.answer}
                    onChange={(e) => changeAnswer(index, { answer: e.target.value })}
                  />
                </Col>
                <Col xs='auto' style={PREFERRED_COLUMN_STYLE}>
                  <Form.Check
                    type='checkbox'
                    id={`answer-preferred-${index}`}
                    aria-label='Preferred'
                    aria-describedby={PREFERRED_HINT_ID}
                    checked={answer.isCorrect}
                    onChange={(e) => changeAnswer(index, { isCorrect: e.target.checked })}
                  />
                </Col>
                <Col xs='auto' style={REMOVE_COLUMN_STYLE}>
                  <Button
                    variant='outline-danger'
                    size='sm'
                    aria-label={`Remove answer ${index + 1}`}
                    disabled={answers.length <= 1}
                    onClick={() => removeAnswer(index)}>
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
            ))}
            <Button variant='outline-secondary' size='sm' onClick={addAnswer}>
              <FaPlus /> Add Answer
            </Button>
            {errors?.answers && (
              <div className='invalid-feedback d-block'>{errors.answers}</div>
            )}
            {props.isEdit && (
              <Form.Text muted className='d-block'>
                Removing an answer hides it from the survey; the responses it already collected stay in the totals.
              </Form.Text>
            )}
          </Form.Group>
          <Button variant='primary' type='submit'>
            Submit
          </Button>
        </Form>
      </Modal.Body>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 999,
            backgroundColor: '#000000c4',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
    </Modal>
  )
}

export default QuestionForm
